/** @file The API other panels and the test hooks call on the mounted viewport (null-safe without one). */
import type { Id } from '@pose2d/format/types';
import type { Vec2 } from '@pose2d/math/types';
import type { ViewportRuntime } from './createViewportRuntime';
import type { PointerKind, ViewportPointer } from './viewportTypes';

type Connected = Pick<ViewportRuntime, 'pick' | 'screenToWorld' | 'pointer' | 'frameSelection' | 'focusBone' | 'commitUnkeyed'>;
let current: Connected | null = null;

/** Every member answers "nothing" (false / null) while no viewport is mounted. */
export const viewportApi = {
  /** Connects the mounted viewport; returns the disconnect. */
  attach(runtime: Connected): () => void {
    current = runtime;
    return () => { if (current === runtime) current = null; };
  },
  /** Frames the selected bones (every bone when none is selected). */
  frameSelection(): boolean { current?.frameSelection(); return current !== null; },
  /** Centres the camera on a bone's joint at the current zoom (hierarchy double-click). */
  focusBone(id: Id): boolean { return current?.focusBone(id) ?? false; },
  /** World point under a canvas pixel (CSS px). */
  screenToWorld(sx: number, sy: number): Vec2 | null { return current?.screenToWorld(sx, sy) ?? null; },
  /** Bone and slot ids under a canvas pixel, by the select tool's rules. */
  pick(sx: number, sy: number): { bone: Id | null; slot: Id | null } { return current?.pick(sx, sy) ?? { bone: null, slot: null }; },
  /** Feeds one pointer sample as the user would. */
  pointer(kind: PointerKind, p: ViewportPointer): boolean { current?.pointer(kind, p); return current !== null; },
  /** Commits the unkeyed pose (the Key button and chord). */
  commitUnkeyed(): void { current?.commitUnkeyed(); },
};
